import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Explanation: This component is meant to prevent accidental page transitions
// which could cause unsaved work to be lost. When this component is mounted
// and the `when` prop is true, it does two things:
//
// 1. Ask for confirmation before allowing "leave page" browser navigation
//    (close/refresh). This uses the window's beforeunload event.
// 2. When unmounting with unsaved changes, prompt before navigation.
//
// Note: useBlocker requires a data router (createBrowserRouter/createHashRouter)
// which we don't currently use. This simpler implementation handles the most
// common case of preventing accidental tab closure.

const DEFAULT_MESSAGE =
  "You have unsaved changes, are you sure you want to leave?";

interface Props {
  when: boolean;
  message?: string;
}

export default function NavigationLock({ when, message }: Props) {
  const confirmMessage = message || DEFAULT_MESSAGE;
  const location = useLocation();
  const navigate = useNavigate();
  const prevLocationRef = useRef(location);

  // Handle browser beforeunload event (close/refresh)
  useEffect(() => {
    if (!when) return;

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [when]);

  // Intercept popstate (browser back/forward) when blocking is active
  useEffect(() => {
    if (!when) return;

    const handlePopState = () => {
      const proceed = window.confirm(confirmMessage);
      if (!proceed) {
        // Push back to the current location to cancel the navigation
        navigate(prevLocationRef.current.pathname, { replace: true });
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [when, confirmMessage, navigate]);

  // Keep track of current location
  useEffect(() => {
    prevLocationRef.current = location;
  }, [location]);

  return null;
}
